import * as React from 'react';
import { connect } from 'react-redux';
import { DataShape } from 'redux-form';
import * as queryString from 'query-string';

import * as autoForm from 'login-utils/autoForm';
import * as featureFlags from 'login-utils/featureFlags';
import * as formUtils from 'login-utils/formUtils';
import * as onboardingForm from 'login-utils/onboardingForm';
import * as specialtyVehicleForm from 'login-utils/specialtyVehicleForm';
import * as sessionUtils from 'login-utils/sessionUtils';
import * as stateUtils from 'login-utils/stateUtils';
import InputMap from 'login-components/Common/Inputs/InputMap';
import connectedSimpleForm from 'login-components/SimpleForm/SimpleForm';
import * as createAccountUtils from './createAccountUtils';

const CORE_USERNAME_PROPERTY = 'email';

const defaultInputs: FormInput[] = [
  {
    ...InputMap.EmailInput,
    identifier: 'email',
    name: CORE_USERNAME_PROPERTY,
    label: 'Email address',
  },
  {
    ...InputMap.EmailInputWithMatch,
    identifier: 'email',
    name: `${CORE_USERNAME_PROPERTY}2`,
    placeholder: 'Confirm email address',
    label: 'Confirm email address',
    validateMatchCaseInsensitive: true,
    validateMatch: CORE_USERNAME_PROPERTY,
  },
  {
    ...InputMap.PasswordInputWithStrength,
    identifier: 'password',
    name: 'password',
    label: 'Password',
  },
];

const termsAndPrivacyAndEConsentRequirementInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent'],
  identifier: 'terms',
  name: 'acceptTermsOfServiceAndEConsent',
};

const termsAndPrivacyPolicyRequirementInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['termsOfUse', 'privacyPolicy'],
  identifier: 'terms',
  name: 'acceptTermsOfServiceAndEConsent',
};

const econsentOnlyRequirementInput: FormInput = {
  ...InputMap.RequirementInput,
  acceptedTermsTypes: ['eConsent'],
  identifier: 'econsent',
  name: 'acceptEConsent',
};

const getCreateAccountInputs = (): FormInput[] => {
  if (featureFlags.isEnabled('truncatedEConsent20180817')) {
    if (featureFlags.isEnabled('econsentOnlyCheckboxForLogin20190115')) {
      return [...defaultInputs, termsAndPrivacyPolicyRequirementInput, econsentOnlyRequirementInput];
    }
    return [...defaultInputs, termsAndPrivacyAndEConsentRequirementInput];
  }
  return defaultInputs;
};

interface CreateAccountStateProps {
  stateLoanType?: LoanType;
  stateAutoProperties?: autoForm.AutoModelProperties;
  stateSpecialtyVehicleProperties?: specialtyVehicleForm.SpecialtyVehicleModelProperties;
}

export class CreateAccount extends React.Component<CreateAccountStateProps & ReactRouter.RouteComponentProps<{}, {}>> {
  private SimpleForm: any;
  private inputs: FormInput[];

  constructor(props) {
    super(props);

    const optionalParams = stateUtils.getOptionalCreationParams(queryString.parse(props.location.search), [
      'referrerId',
      'referringRealtor',
      'app_source',
      'applicationTemplateId',
      'displayLink',
      'loanType',
      'shoppedProductId',
      'comid',
    ]);

    this.inputs = getCreateAccountInputs();
    this.SimpleForm = connectedSimpleForm(this.inputs, optionalParams);
  }

  onSubmit = async (values: DataShape) => {
    const { stateAutoProperties, stateSpecialtyVehicleProperties, stateLoanType } = this.props;

    const modelProperties = (() => {
      switch (stateLoanType) {
        case 'AUTO':
          return stateAutoProperties;
        case 'SPECIALTY_VEHICLE':
          return stateSpecialtyVehicleProperties;
        default:
          return null;
      }
    })();

    await createAccountUtils.submitCreateAccount({
      ...values,
      ...(stateLoanType && { loanType: stateLoanType }),
      ...(modelProperties && { modelProperties }),
    });
  };

  componentDidMount() {
    const { location } = this.props;
    // react-router puts the query string after the hash, so it's not sent to the server
    sessionUtils.saveQueryToSession(queryString.parse(location.search));
  }

  renderForm() {
    const { stateLoanType } = this.props;
    return (
      <this.SimpleForm
        inputs={this.inputs}
        submitFn={this.onSubmit}
        mobileTitle={'Enter your email to get started'}
        actionText={'Create Account'}
        disclaimerText={formUtils.getEmailDisclaimerText()}
        loanType={stateLoanType}
      />
    );
  }

  render() {
    return <div className="it-CreateAccount">{this.renderForm()}</div>;
  }
}

export default connect((state: ReduxState) => ({
  stateLoanType: onboardingForm.getLoanType(state),
  stateAutoProperties: autoForm.getModelProperties(state),
  stateSpecialtyVehicleProperties: specialtyVehicleForm.getModelProperties(state),
}))(CreateAccount);
